import { useId, type ReactNode } from 'react'

import { cx } from '../lib/cx'
import { Icon } from './Icon'

/**
 * Campos de formulario del panel.
 *
 * El design system arrancó sin primitivas de formulario — las cinco pantallas de
 * lectura no las necesitaban — así que **dos** módulos escribieron las suyas en
 * paralelo, cada uno con un comentario que decía que su lugar era acá. Y no eran
 * equivalentes: una asociaba la ayuda con `aria-describedby`, marcaba
 * `aria-invalid` y mostraba el error con icono y texto; la otra no tenía error
 * del todo. Un formulario que valida y uno que no, en el mismo panel, es la clase
 * de diferencia que nadie ve hasta que alguien tipea mal.
 *
 * Quedó la versión completa. Reglas que ninguna variante negocia: label real
 * asociada por `id`, error con `role="alert"` referenciado por
 * `aria-describedby`, `aria-invalid` en el control, y el estado de error nunca
 * comunicado solo con el borde rojo.
 */

const CONTROL =
  'w-full rounded-md border bg-surface px-2 py-1 text-meta text-text placeholder:text-muted hover:border-n300 focus:outline-none focus:ring-2 focus:ring-accent-text'

interface Base {
  label: string
  /** Ayuda bajo el control: qué se espera, o la variable de entorno. */
  ayuda?: ReactNode
  /** Mensaje de error del campo. Rojo + icono + texto, nunca solo el borde. */
  error?: string | null
  requerido?: boolean
  className?: string
}

function hayError(error: string | null | undefined): boolean {
  return error !== null && error !== undefined && error !== ''
}

function Envoltorio({
  label,
  ayuda,
  error,
  requerido,
  className,
  controlId,
  ayudaId,
  children,
}: Base & {
  controlId: string
  ayudaId: string
  children: ReactNode
}) {
  return (
    <div className={cx('min-w-0', className)}>
      <label htmlFor={controlId} className="rotulo mb-1 block">
        {label}
        {requerido === true ? (
          <span className="ml-1 font-normal normal-case tracking-normal text-muted">
            (obligatorio)
          </span>
        ) : null}
      </label>
      {children}
      {ayuda !== undefined && !hayError(error) ? (
        <p id={ayudaId} className="mt-1 text-micro text-muted-strong">
          {ayuda}
        </p>
      ) : null}
      {hayError(error) ? (
        <p
          id={ayudaId}
          role="alert"
          className="mt-1 inline-flex items-start gap-1 text-micro font-semibold text-danger-text"
        >
          <Icon name="critico" size={11} className="mt-px" />
          {error}
        </p>
      ) : null}
    </div>
  )
}

function bordeDe(error: string | null | undefined): string {
  return hayError(error) ? 'border-danger' : 'border-border'
}

export interface TextFieldProps extends Base {
  value: string
  onChange: (valor: string) => void
  placeholder?: string
  tipo?: 'text' | 'number' | 'date'
  /**
   * `decimal` abre el teclado numérico sin volver el campo `type="number"`: los
   * umbrales se escriben en es-AR (`75.000`) y el parseo es nuestro, no el que
   * impone el navegador.
   */
  inputMode?: 'text' | 'decimal'
  disabled?: boolean
  /** Filas > 1 renderiza un textarea. */
  filas?: number
  maxLength?: number
  /** Solo para `number`: paso del incremento. */
  step?: string
}

export function TextField({
  value,
  onChange,
  placeholder,
  tipo = 'text',
  inputMode,
  disabled,
  filas,
  maxLength,
  step,
  ...base
}: TextFieldProps) {
  const id = useId()
  const ayudaId = `${id}-ayuda`
  const describedBy =
    base.ayuda !== undefined || hayError(base.error) ? ayudaId : undefined

  return (
    <Envoltorio {...base} controlId={id} ayudaId={ayudaId}>
      {filas !== undefined && filas > 1 ? (
        <textarea
          id={id}
          rows={filas}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          aria-invalid={hayError(base.error) ? true : undefined}
          aria-describedby={describedBy}
          onChange={(evento) => onChange(evento.target.value)}
          className={cx(
            CONTROL,
            bordeDe(base.error),
            'resize-y leading-relaxed',
          )}
        />
      ) : (
        <input
          id={id}
          type={tipo}
          inputMode={inputMode}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={maxLength}
          step={step}
          aria-invalid={hayError(base.error) ? true : undefined}
          aria-describedby={describedBy}
          onChange={(evento) => onChange(evento.target.value)}
          className={cx(
            CONTROL,
            bordeDe(base.error),
            (tipo === 'number' || inputMode === 'decimal') && 'tabular',
            disabled === true && 'cursor-not-allowed bg-n100 text-muted-strong',
          )}
        />
      )}
    </Envoltorio>
  )
}

export interface OpcionCampo {
  value: string
  label: string
}

export interface SelectFieldProps extends Base {
  value: string
  onChange: (valor: string) => void
  opciones: readonly OpcionCampo[]
  /** Opción vacía inicial. Sin esto el select arranca en la primera opción, que
   *  es cómo se termina asignando un seguimiento a quien salió sorteado. */
  placeholder?: string
}

export function SelectField({
  value,
  onChange,
  opciones,
  placeholder,
  ...base
}: SelectFieldProps) {
  const id = useId()
  const ayudaId = `${id}-ayuda`
  const describedBy =
    base.ayuda !== undefined || hayError(base.error) ? ayudaId : undefined

  return (
    <Envoltorio {...base} controlId={id} ayudaId={ayudaId}>
      <select
        id={id}
        value={value}
        aria-invalid={hayError(base.error) ? true : undefined}
        aria-describedby={describedBy}
        onChange={(evento) => onChange(evento.target.value)}
        className={cx(CONTROL, bordeDe(base.error), 'h-7 py-0')}
      >
        {placeholder !== undefined ? (
          <option value="">{placeholder}</option>
        ) : null}
        {opciones.map((opcion) => (
          <option key={opcion.value} value={opcion.value}>
            {opcion.label}
          </option>
        ))}
      </select>
    </Envoltorio>
  )
}
